import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  PortalAwardOutcomeDto,
  PortalLifecycleStatus,
  type PortalAwardLetterDto,
  type PortalSourcingEventDetailDto,
  type WithdrawnPortalAwardLetterDto,
  useGetPortalAwardLetterQuery,
} from "../../../../autogen/bff-api";
import { useDisplayOsloDate } from "../../../../common/formatting/useDisplayOsloDate";
import { usePortalProcurementTerms } from "../../../../common/terminology/useProcurementTerms";
import { AwardLetterPreview } from "../../../sourcing-event/award/AwardLetterPreview";
import { winnerNamesText, withdrawnStandstill } from "../../../sourcing-event/award/awardLetterTemplates";
import { prizeLabel } from "../../buyer/wizard/utils/designContest";
import { AwardOutcomeBanner } from "./AwardOutcomeBanner";
import { awardNotificationCriteria } from "./awardNotificationCriteria";
import { isPrivatePortalEvent } from "./PrivateInviteCta";
import { portalStandstillTerms } from "./portalStandstillTerms";

export const EventAwardNotificationSection = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const { data } = useGetPortalAwardLetterQuery({ eventId: event.id });
  if (!data || (!data.letter && !data.outcome && !data.withdrawnLetter && !data.outcomeAwaitsLetter)) return null;
  const withdrawn = data.outcome === PortalAwardOutcomeDto.Withdrawn;
  const cancelled = event.lifecycle.status === PortalLifecycleStatus.Cancelled;

  return (
    <Stack spacing="3">
      <Text fontSize="lg" fontWeight="semibold" color="smText">
        {t("Result")}
      </Text>
      <AwardOutcomeBanner event={event} award={data} />
      {data.outcomeAwaitsLetter && !cancelled && (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
          <Text fontWeight="semibold" color="smText">
            {t("The buyer has made a decision")}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("You will see the outcome in your award letter, with its reasons. You will be notified by email when it is sent.")}
          </Text>
        </Box>
      )}
      {withdrawn || data.outcomeAwaitsLetter ? null : data.letter ? (
        cancelled ? (
          <LetterThatNoLongerApplies
            title={t("Award notification for the cancelled competition")}
            explanation={t("This letter was sent before the buyer cancelled the competition. It no longer applies.")}
          >
            <SentAwardLetter event={event} award={data} asRecord />
          </LetterThatNoLongerApplies>
        ) : (
          <SentAwardLetter event={event} award={data} />
        )
      ) : (
        !cancelled &&
        !event.awardCannotBecomeCallOff &&
        data.letterPending && (
          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
            <Text fontWeight="semibold" color="smText">
              {t("Your award letter is on its way")}
            </Text>
            <Text fontSize="sm" color="smMuted">
              {t("You will be notified by email when the reasons are ready.")}
            </Text>
          </Box>
        )
      )}
      {data.withdrawnLetter && <WithdrawnAwardLetter event={event} award={data} withdrawn={data.withdrawnLetter} />}
    </Stack>
  );
};

const WithdrawnAwardLetter = ({
  event,
  award,
  withdrawn,
}: {
  event: PortalSourcingEventDetailDto;
  award: PortalAwardLetterDto;
  withdrawn: WithdrawnPortalAwardLetterDto;
}) => {
  const { t } = useTranslation();
  const terms = usePortalProcurementTerms(event);
  const letter = withdrawn.letter;
  const criteria = awardNotificationCriteria(
    withdrawn.evaluation?.criteria ?? [],
    event.awardCriteria?.criteria ?? [],
    { criterion: t("Criterion") || "", price: t("Price") || "" },
    withdrawn.justifiedCriteria,
  );

  return (
    <LetterThatNoLongerApplies
      title={t("Award notification for the withdrawn decision")}
      explanation={t("This letter was sent for the award decision the buyer has reversed. It no longer applies.")}
    >
      <AwardLetterPreview
        type={letter.type}
        forRejectedTender={letter.forRejectedTender}
        chosenTenderAdvantages={letter.chosenTenderAdvantages}
        supplierName={letter.supplierName || ""}
        winnerName={winnerNamesText(withdrawn.winnerNames ?? [])}
        eventTitle={event.title}
        senderName={event.buyerName}
        signerName={withdrawn.signerName ?? undefined}
        scoreHeading={terms.scorePerAwardCriterion}
        criterionJustifications={letter.content.criterionJustifications ?? {}}
        freeText={letter.content.freeText}
        rankingDepartureExplanation={letter.content.rankingDepartureExplanation}
        criteria={criteria}
        evaluation={withdrawn.evaluation}
        sentAt={letter.sentAt}
        sentClauses={letter.sentClauses}
        clauseContext={{
          eventRunsPrivateFlow: isPrivatePortalEvent(event),
          outsideProcurementAct: event.outsideProcurementAct ?? false,
          buyerRunsPublicProcurements: event.buyerRunsPublicProcurements ?? false,
          regulation: award.regulation,
          designContest: !!event.designContest,
          designContestPrizes: (event.designContest?.prizes ?? []).map(prizeLabel),
          standstill: withdrawnStandstill(
            award.buyerSetsStandstill ?? false,
            award.choosesVoluntaryStandstill ?? false,
            withdrawn.standstillDays,
          ),
        }}
      />
    </LetterThatNoLongerApplies>
  );
};

const LetterThatNoLongerApplies = ({
  title,
  explanation,
  children,
}: {
  title: string;
  explanation: string;
  children: ReactNode;
}) => (
  <Accordion allowToggle>
    <AccordionItem border="1px solid" borderColor="smBorder" rounded="xl" overflow="hidden" bg="smBackground">
      <AccordionButton px="4" py="3" gap="3" textAlign="left" _hover={{ bg: "smBackgroundSecondary" }}>
        <Stack spacing="0.5" flex="1">
          <Text fontWeight="semibold" fontSize="sm" color="smText">
            {title}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {explanation}
          </Text>
        </Stack>
        <AccordionIcon color="smMuted" />
      </AccordionButton>
      <AccordionPanel px="4" pb="4" pt="3" borderTop="1px solid" borderColor="smBorder">
        {children}
      </AccordionPanel>
    </AccordionItem>
  </Accordion>
);

const SentAwardLetter = ({
  event,
  award,
  asRecord = false,
}: {
  event: PortalSourcingEventDetailDto;
  award: PortalAwardLetterDto;
  asRecord?: boolean;
}) => {
  const { t } = useTranslation();
  const displayDate = useDisplayOsloDate();
  const terms = usePortalProcurementTerms(event);
  const letter = award.letter;
  if (!letter) return null;

  const criteria = awardNotificationCriteria(
    award.evaluation?.criteria ?? [],
    event.awardCriteria?.criteria ?? [],
    { criterion: t("Criterion") || "", price: t("Price") || "" },
    award.justifiedCriteria,
  );
  const standstillExpiresAt = award.standstillExpiresAt ?? undefined;

  return (
    <Stack spacing="3">
      {!asRecord && (
        <Text fontSize="md" fontWeight="semibold" color="smText">
          {t("Your award notification")}
        </Text>
      )}
      <AwardLetterPreview
        type={letter.type}
        forRejectedTender={letter.forRejectedTender}
        chosenTenderAdvantages={letter.chosenTenderAdvantages}
        supplierName={letter.supplierName || ""}
        winnerName={winnerNamesText(award.winnerNames ?? [])}
        eventTitle={event.title}
        senderName={event.buyerName}
        signerName={award.signerName ?? event.buyerContactName}
        scoreHeading={terms.scorePerAwardCriterion}
        criterionJustifications={letter.content.criterionJustifications ?? {}}
        freeText={letter.content.freeText}
        rankingDepartureExplanation={letter.content.rankingDepartureExplanation}
        criteria={criteria}
        evaluation={award.evaluation}
        sentAt={letter.sentAt}
        sentClauses={letter.sentClauses}
        clauseContext={{
          eventRunsPrivateFlow: isPrivatePortalEvent(event),
          outsideProcurementAct: event.outsideProcurementAct ?? false,
          buyerRunsPublicProcurements: event.buyerRunsPublicProcurements ?? false,
          regulation: award.regulation,
          designContest: !!event.designContest,
          designContestPrizes: (event.designContest?.prizes ?? []).map(prizeLabel),
          standstill: portalStandstillTerms(award),
        }}
      />
      {standstillExpiresAt && !award.standstillExemption && !asRecord && (
        <Box bg="smBackgroundSecondary" rounded="md" border="1px solid" borderColor="smBorder" p="4">
          <Text fontSize="sm" color="smText">
            {t("The award decision is not final until {{date}}, during which it may be challenged.", {
              date: displayDate(standstillExpiresAt),
            })}
          </Text>
        </Box>
      )}
    </Stack>
  );
};
